import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useWallet } from '../components/WalletContext';
import Spinner from '../components/Spinner';
import '../styles/CollectionDetailPage.css';
import { getPrimaryColorFromImage } from '../utils/colorUtils';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import skyGalleryImage from '../assets/skygallery.png';
import klevernftImage from '../assets/klever.png';
import minthreeImage from '../assets/minthree.png';
import xportImage from '../assets/xport.png';
import RareCanvasImage from '../assets/logoicon2.png';
import kDEXImage from '../assets/kdex.png';
import worldImage from '../assets/world.png';
import artGalleryImage from '../assets/artgallery.png';
import telegramLogo from '../assets/telegramlogo.png';
import websiteLogo from '../assets/websitelogo.png';
import discordLogo from '../assets/discordlogo.png';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const marketplaceNames = {
  '417b70c0eb7a33cb': 'Rare Canvas',
  'd4f2bab340c55fde': 'Klever NFT',
  'aa68ad853a09988c': 'Xport',
  'd2a04fe890a6edda': 'NFT Art Gallery',
  '2936933ee43db509': 'Minthree',
  '7c353c02770da029': 'Sky Gallery',
  '116056b257d9f6d5': 'kDEX',
  '81376f526cf47730': 'World Dex'
};

const marketplaceImages = {
  '7c353c02770da029': skyGalleryImage,
  'd4f2bab340c55fde': klevernftImage,
  '2936933ee43db509': minthreeImage,
  'aa68ad853a09988c': xportImage,
  '417b70c0eb7a33cb': RareCanvasImage,
  '116056b257d9f6d5': kDEXImage,
  '81376f526cf47730': worldImage,
  'd2a04fe890a6edda': artGalleryImage,
};

const CollectionDetailPage = () => {
  const { collectionId } = useParams();
  const { walletAddress } = useWallet();

  // Main states
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [activeTab, setActiveTab] = useState('items');
  const [activeSubTab, setActiveSubTab] = useState('sales');
  const [activity, setActivity] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transactionPage, setTransactionPage] = useState(1);
  const [totalTransactionPages, setTotalTransactionPages] = useState(0);
  const [nftPrices, setNftPrices] = useState({});
  const [logoUrl, setLogoUrl] = useState('');
  const [bannerUri, setBannerUri] = useState('');
  const [gradientBackground, setGradientBackground] = useState('');
  const [assetName, setAssetName] = useState('');
  const [totalVolume, setTotalVolume] = useState(0);
  const [floorPrice, setFloorPrice] = useState(0);
  const [items, setItems] = useState(0);
  const [owners, setOwners] = useState(0);
  const [selectedMarketplace, setSelectedMarketplace] = useState('417b70c0eb7a33cb');
  const [telegramUri, setTelegramUri] = useState('');
  const [websiteUri, setWebsiteUri] = useState('');
  const [discordUri, setDiscordUri] = useState('');
  const [imageUrl, setImageUrl] = useState(null);
  const [cache, setCache] = useState({});
  const [activityPage, setActivityPage] = useState(1);
  const [totalActivityPages, setTotalActivityPages] = useState(0);
  const [traits, setTraits] = useState({});
  const [selectedTraits, setSelectedTraits] = useState({});
  const [sortOrder, setSortOrder] = useState('default');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [showAuctionsOnly, setShowAuctionsOnly] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [marketplaceDropdownVisible, setMarketplaceDropdownVisible] = useState(false);
  
  // New advanced states
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [priceHistory, setPriceHistory] = useState([]);
  const [listedCount, setListedCount] = useState(0);
  const [avgPrice, setAvgPrice] = useState(0);
  const [volumeChange24h, setVolumeChange24h] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [salesHistory, setSalesHistory] = useState([]);
  const [topHolders, setTopHolders] = useState([]);
  const [royaltyInfo, setRoyaltyInfo] = useState(null);

  // Fetch data on load and when dependencies change
  useEffect(() => {
    fetchData(currentPage);
    fetchCollectionStats();
    fetchActivityData();
    fetchTransactions();
    fetchSalesHistory();
  }, [collectionId, currentPage, activityPage, transactionPage, selectedMarketplace, sortOrder]);

  useEffect(() => {
    // Reset pagination when switching tabs
    setCurrentPage(1);
    setActivityPage(1);
    setTransactionPage(1);
  }, [activeTab]);

  useEffect(() => {
    // Generate gradient background if no banner is provided
    if (logoUrl && !bannerUri) {
      generateGradientFromLogo(logoUrl);
    }
  }, [logoUrl, bannerUri]);

  // Asset and metadata functions
  const fetchAssetList = async () => {
    try {
      // First try direct asset lookup (most efficient)
      try {
        const directResponse = await axios.get(
          `https://api.mainnet.klever.org/v1.0/assets/${collectionId}`
        );
        const asset = directResponse.data?.data?.asset;
        if (asset) {
          setAssetName(asset.name);
          setTelegramUri(extractUri(asset, 'telegram'));
          setWebsiteUri(extractUri(asset, 'website'));
          setDiscordUri(extractUri(asset, 'discord'));
          setBannerUri(extractUri(asset, 'banner'));
          setLogoUrl(modifyLogoUrl(asset.logo));

          // Set royalty info
          if (asset.royalties) {
            setRoyaltyInfo({
              marketPercentage: asset.royalties.marketPercentage / 100,
              transferPercentage: asset.royalties.transferPercentage / 100
            });
          }
          return asset;
        }
      } catch (directError) {
        console.log('Direct asset lookup failed, trying list search...');
      }

      // Fallback to searching asset list
      const baseUrl = 'https://api.mainnet.klever.org/v1.0/assets/list?limit=100';
      for (let p = 1; p <= 10; p++) {
        const response = await axios.get(`${baseUrl}&page=${p}`);
        const assetList = response.data.data.assets || [];
        const matchingAsset = assetList.find(asset => asset.assetId === collectionId);
        if (matchingAsset) {
          setAssetName(matchingAsset.name);
          setTelegramUri(extractUri(matchingAsset, 'telegram'));
          setWebsiteUri(extractUri(matchingAsset, 'website'));
          setDiscordUri(extractUri(matchingAsset, 'discord'));
          setBannerUri(extractUri(matchingAsset, 'banner'));
          setLogoUrl(modifyLogoUrl(matchingAsset.logo));

          // Set royalty info
          if (matchingAsset.royalties) {
            setRoyaltyInfo({
              marketPercentage: matchingAsset.royalties.marketPercentage / 100,
              transferPercentage: matchingAsset.royalties.transferPercentage / 100
            });
          }
          return matchingAsset;
        }
        // Stop if we've reached the end
        if (assetList.length < 100) break;
      }
      return null;
    } catch (error) {
      console.error('Error fetching asset list:', error);
      return null;
    }
  };

  const extractUri = (asset, key) => {
    const found = asset.uris.find(uri => uri.key.toLowerCase() === key.toLowerCase());
    return found ? found.value : null;
  };

  const modifyLogoUrl = (logoUri) => {
    return logoUri && logoUri.includes("klever-mint.mypinata.cloud")
      ? logoUri.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io")
      : logoUri;
  };

  const generateGradientFromLogo = async (logoUrl) => {
    try {
      const colors = await getPrimaryColorFromImage(logoUrl);
      if (colors && colors.length > 0) {
        setGradientBackground(`linear-gradient(135deg, ${colors.join(', ')})`);
      }
    } catch (error) {
      console.error('Error generating gradient:', error);
    }
  };

  const fetchMetadata = async (asset, nonce) => {
    if (cache[nonce]) {
      setImageUrl(cache[nonce].image);
      return cache[nonce];
    }
    try {
      let metadataUri = getMetadataUri(asset, nonce);
      if (!metadataUri) return null;
      const response = await fetch(metadataUri);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const metadata = await response.json();
      metadata.image = modifyImageUrl(metadata.image);
      setImageUrl(metadata.image);
      const price = await fetchNftPrice(`${collectionId}/${nonce}`);
      setCache(prev => ({ ...prev, [nonce]: { ...metadata, price } }));

      // Extract and store traits
      if (metadata.attributes) {
        const extracted = {};
        metadata.attributes.forEach(attr => {
          extracted[attr.trait_type] = extracted[attr.trait_type]
            ? new Set([...extracted[attr.trait_type], attr.value])
            : new Set([attr.value]);
        });
        setTraits(prev => {
          const updated = { ...prev };
          Object.entries(extracted).forEach(([k, v]) => {
            updated[k] = prev[k] ? new Set([...prev[k], ...v]) : v;
          });
          return updated;
        });
      }
      return { ...metadata, price };
    } catch (error) {
      console.error('Error fetching metadata:', error);
      return null;
    }
  };

  const getMetadataUri = (asset, nonce) => {
    if (!asset?.uris) return null;
    const metaObj = asset.uris.find(uri => uri.key.toLowerCase() === 'metadata');
    let uri = metaObj ? metaObj.value : null;
    if (!uri) return null;

    // Handle special IPFS hash case
    if (uri === "QmNaa2KQ6NkjjESpPHEnAow9hivnsAkq2Gd6R26cHG28Er") {
      return `https://ipfs.io/ipfs/${uri}/${nonce}.json`;
    }

    // Handle ipfs:// prefix
    if (uri.startsWith('ipfs://')) {
      uri = uri.replace('ipfs://', 'https://ipfs.io/ipfs/');
    }

    // Handle klever-mint.mypinata.cloud URLs
    if (uri.includes("klever-mint.mypinata.cloud")) {
      uri = uri.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
    }

    // For URLs that already include the base path, append nonce
    // Handle cases like: https://tgcworld.org/item-metadata/pmc -> https://tgcworld.org/item-metadata/pmc/14
    // Handle cases like: https://ipfs.io/ipfs/xxx -> https://ipfs.io/ipfs/xxx/14
    return `${uri}/${nonce}`;
  };

  const modifyImageUrl = (imageUri) => {
    if (!imageUri) return null;
    if (imageUri.includes("www.lovemonsternft.com")) {
      const imageName = imageUri.split('/').pop();
      return `https://ipfs.io/ipfs/QmWVmUDPBeQzv6fG93JxQxFVee8b6smFD3RQosQXJHiZTJ/${imageName}`;
    } else if (imageUri.includes("klever-mint.mypinata.cloud")) {
      return imageUri.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
    } else if (imageUri.startsWith('ipfs://')) {
      return imageUri.replace('ipfs://', 'https://ipfs.io/ipfs/');
    }
    return imageUri;
  };

  const fetchNftPrice = async (uniqueNftId) => {
    try {
      const url = `https://api.mainnet.klever.org/v1.0/marketplaces/nfts/activity?nftId=${uniqueNftId}&sort=desc&limit=10&page=1`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const rawPrice = data.data.history[0]?.contract[0]?.parameter?.price;
      if (rawPrice) {
        const price = rawPrice / 1000000;
        const formattedPrice = Number.isInteger(price)
          ? price.toLocaleString('en-US')
          : price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
        setNftPrices(prev => ({ ...prev, [uniqueNftId]: formattedPrice }));
        return formattedPrice;
      }
      return null;
    } catch (error) {
      console.error('Error fetching NFT price:', error);
      return null;
    }
  };

  const fetchData = async (page) => {
    setLoading(true);
    try {
      const asset = await fetchAssetList();
      if (!asset) {
        setLoading(false);
        return;
      }
      const response = await axios.get(
        `https://api.mainnet.klever.org/marketplaces/${selectedMarketplace}?collection=${collectionId}&price=desc&limit=50&page=${page}`
      );

      // Handle different API response structures
      const rawAssets = response.data?.data?.assets?.assets || response.data?.data?.assets || {};

      // Convert to array if it's an object, filter out invalid entries
      const assetsArray = Array.isArray(rawAssets)
        ? rawAssets
        : Object.values(rawAssets).filter(item => item && item.nftNonce !== undefined);

      if (assetsArray.length === 0) {
        setNfts([]);
        setTotalPages(1);
        setLoading(false);
        return;
      }

      const initialNfts = assetsArray.map(assetDetails => ({
        nonce: assetDetails.nftNonce,
        metadata: null,
        loadingMetadata: true,
        orderId: assetDetails.orderId
      }));

      setNfts(initialNfts);
      setTotalPages(response.data?.pagination?.totalPages || 1);

      // Load metadata for each NFT
      initialNfts.forEach(async nft => {
        try {
          const metadata = await fetchMetadata(asset, nft.nonce);
          const transactionResponse = await axios.get(
            `https://api.mainnet.klever.org/v1.0/transaction/list?orderid=${nft.orderId}`
          );
          const transactions = transactionResponse.data.data.transactions;
          const now = Math.floor(Date.now() / 1000);

          // Check if expired and get marketType
          let isExpired = false;
          let marketType = 0; // Default to fixed price
          let price = null;

          transactions.forEach(tx => {
            tx.contract.forEach(contract => {
              if (contract.parameter.endTime && contract.parameter.endTime <= now) {
                isExpired = true;
              }
              if (contract.parameter.marketType !== undefined) {
                marketType = contract.parameter.marketType;
              }
              if (contract.parameter.price) {
                price = contract.parameter.price / 1000000;
              }
            });
          });

          updateMetadataForNonce(nft.nonce, {
            ...metadata,
            expired: isExpired,
            orderId: nft.orderId,
            marketType: marketType,
            price: price
          });
        } catch (error) {
          console.error(`Error fetching data for nonce ${nft.nonce}:`, error);
          updateMetadataForNonce(nft.nonce, { metadata: null, expired: false, orderId: nft.orderId, marketType: 0 });
        }
      });
    } catch (error) {
      console.error('Error fetching NFTs:', error);
      setNfts([]);
      setTotalPages(1);
    }
    setLoading(false);
  };

  const updateMetadataForNonce = (nonce, metadata) => {
    setNfts(prev =>
      prev.map(nft => (nft.nonce === nonce ? { ...nft, metadata, loadingMetadata: false } : nft))
    );
  };

  const fetchCollectionStats = useCallback(async () => {
    try {
      const marketplaceIds = [
        'd4f2bab340c55fde', '7c353c02770da029', '2936933ee43db509',
        '417b70c0eb7a33cb', '116056b257d9f6d5', 'aa68ad853a09988c',
        '81376f526cf47730', 'd2a04fe890a6edda'
      ];
      const statsPromises = marketplaceIds.map(id =>
        axios.get(`https://api.mainnet.klever.org/v1.0/marketplaces/${id}/stats`)
      );
      const statsResponses = await Promise.all(statsPromises);
      
      let combinedStats = {
        totalVolume: 0,
        items: 0,
        owners: 0,
        floorPrice: Infinity,
        listedCount: 0,
        prices: []
      };

      statsResponses.forEach(response => {
        const collectionStats = response.data.data.stats.find(
          stat => stat.assetId === collectionId
        );
        if (collectionStats) {
          combinedStats.totalVolume += collectionStats.volumeTotal || 0;
          combinedStats.items += collectionStats.items || 0;
          combinedStats.owners += collectionStats.owners || 0;
          combinedStats.listedCount += collectionStats.listing || 0;
          if (collectionStats.floor && collectionStats.floor > 0) {
            if (collectionStats.floor < combinedStats.floorPrice) {
              combinedStats.floorPrice = collectionStats.floor;
            }
            combinedStats.prices.push(collectionStats.floor);
          }
        }
      });

      setTotalVolume(combinedStats.totalVolume);
      setItems(combinedStats.items);
      setOwners(combinedStats.owners);
      setFloorPrice(combinedStats.floorPrice === Infinity ? 0 : combinedStats.floorPrice);
      setListedCount(combinedStats.listedCount);
      
      // Calculate average price
      if (combinedStats.prices.length > 0) {
        const avg = combinedStats.prices.reduce((a, b) => a + b, 0) / combinedStats.prices.length;
        setAvgPrice(avg);
      }
    } catch (error) {
      console.error('Error fetching collection stats:', error);
    }
  }, [collectionId]);

  // Fetch SALES (Type 17 - Buy transactions)
  const fetchActivityData = async () => {
    try {
      const limit = 100;
      const response = await axios.get(
        `https://api.mainnet.klever.org/v1.0/transaction/list?limit=${limit}&type=17&status=success&asset=KLV&page=${activityPage}`
      );
      const filtered = response.data.data.transactions.filter(tx => {
        const isMarketBuy = tx.contract.some(contract =>
          contract.type === 17 && contract.parameter.buyType === "MarketBuy"
        );
        const includesCollection = tx.receipts.some(r =>
          r.assetId && r.assetId.startsWith(`${collectionId}/`)
        );
        return isMarketBuy && includesCollection;
      }).map(tx => {
        const marketplaceReceipt = tx.receipts.find(r => r.marketplaceId);
        const buyContract = tx.contract.find(c => c.type === 17);
        const assetReceipt = tx.receipts.find(r => r.assetId && r.assetId.startsWith(`${collectionId}/`));
        return {
          ...tx,
          marketplaceId: marketplaceReceipt ? marketplaceReceipt.marketplaceId : null,
          contract: buyContract,
          assetId: assetReceipt ? assetReceipt.assetId : null
        };
      });
      setActivity(filtered);
      setTotalActivityPages(response.data.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Error fetching sales data:', error);
      setActivity([]);
      setTotalActivityPages(1);
    }
  };

  // Fetch LISTINGS (Type 18 - Sell transactions) with status
  const fetchTransactions = async () => {
    try {
      // The transaction API doesn't support asset filter directly
      // We need to fetch recent sell transactions and filter by collection
      const limit = 100;
      const response = await axios.get(
        `https://api.mainnet.klever.org/v1.0/transaction/list?limit=${limit}&type=18&status=success&page=${transactionPage}`
      );

      const now = Math.floor(Date.now() / 1000);
      const txList = response.data.data.transactions || [];

      console.log('Listings API raw response:', txList.length, 'total transactions');

      // Filter and process transactions for this collection
      const enriched = [];

      txList.forEach(tx => {
        const marketplaceReceipt = tx.receipts?.find(r => r.marketplaceId);
        const marketplaceId = marketplaceReceipt?.marketplaceId || null;

        // Process each sell contract in the transaction
        tx.contract?.forEach(contract => {
          if (contract.type !== 18) return;

          const assetId = contract.parameter?.assetId;
          if (!assetId) return;

          // Check if this contract is for our collection
          const [assetCollectionId] = assetId.split('/');
          if (assetCollectionId !== collectionId) return;

          // Find matching receipt for this specific asset
          const assetReceipt = tx.receipts?.find(r => r.assetId === assetId);
          const orderId = assetReceipt?.orderId || null;

          const endTime = contract.parameter.endTime;
          const marketType = contract.parameter.marketType;
          // marketType can be string "BuyItNowMarket" or number 0 for fixed, 1 for auction
          const isAuction = marketType === 1 || marketType === 'AuctionMarket';

          // Determine listing status
          let listingStatus = 'active';
          if (endTime && endTime <= now) {
            listingStatus = 'expired';
          }

          enriched.push({
            hash: tx.hash,
            timestamp: tx.timestamp,
            sender: tx.sender,
            contract,
            marketplaceId,
            isAuction,
            orderId,
            listingStatus,
            endTime
          });
        });
      });

      console.log('Filtered listings for', collectionId + ':', enriched.length);
      setTransactions(enriched);
      setTotalTransactionPages(response.data.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Error fetching listings data:', error);
      setTransactions([]);
      setTotalTransactionPages(1);
    }
  };

  // Fetch Sales History for Charts
  const fetchSalesHistory = async () => {
    try {
      const response = await axios.get(
        `https://api.mainnet.klever.org/v1.0/transaction/list?limit=500&type=17&status=success`
      );

      const transactions = response.data.data.transactions || [];
      const now = Date.now();
      const cutoff = now - (30 * 24 * 60 * 60 * 1000); // 30 days

      // Group sales by day
      const salesByDay = {};
      const holderCounts = {};

      transactions.forEach(tx => {
        const timestamp = tx.timestamp * 1000;

        // Check if this is for our collection
        const receipt = tx.receipts?.find(r =>
          r.assetId && r.assetId.startsWith(`${collectionId}/`)
        );
        if (!receipt) return;

        const buyContract = tx.contract?.find(c => c.typeString === 'BuyContractType');
        if (!buyContract || buyContract.parameter?.buyType !== 'MarketBuy') return;

        // Track top holders
        const buyer = tx.sender;
        holderCounts[buyer] = (holderCounts[buyer] || 0) + 1;

        // Only include in chart if within timeframe
        if (timestamp < cutoff) return;

        const price = (buyContract.parameter?.amount || 0) / 1000000;
        const dateKey = new Date(timestamp).toISOString().split('T')[0];

        if (!salesByDay[dateKey]) {
          salesByDay[dateKey] = { volume: 0, sales: 0, prices: [] };
        }

        salesByDay[dateKey].volume += price;
        salesByDay[dateKey].sales += 1;
        salesByDay[dateKey].prices.push(price);
      });

      // Calculate floor per day
      Object.keys(salesByDay).forEach(day => {
        const dayData = salesByDay[day];
        dayData.floor = dayData.prices.length > 0 ? Math.min(...dayData.prices) : 0;
        dayData.avgPrice = dayData.prices.length > 0
          ? dayData.prices.reduce((a, b) => a + b, 0) / dayData.prices.length
          : 0;
      });

      // Convert to sorted array
      const history = Object.entries(salesByDay)
        .map(([date, data]) => ({ date, ...data }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      setSalesHistory(history);

      // Set top holders
      const holders = Object.entries(holderCounts)
        .map(([address, count]) => ({ address, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
      setTopHolders(holders);

    } catch (error) {
      console.error('Error fetching sales history:', error);
      setSalesHistory([]);
    }
  };

  // Filter and sort NFTs
  const filteredNfts = useMemo(() => {
    let filtered = [...nfts];
    
    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(nft => 
        nft.nonce.toString().includes(searchQuery) ||
        (nft.metadata?.name && nft.metadata.name.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    // Apply trait filters
    Object.entries(selectedTraits).forEach(([trait, value]) => {
      if (value) {
        filtered = filtered.filter(nft => {
          if (!nft.metadata?.attributes) return false;
          const attr = nft.metadata.attributes.find(a => a.trait_type === trait);
          return attr && attr.value === value;
        });
      }
    });

    // Apply active-only filter
    if (showActiveOnly) {
      filtered = filtered.filter(nft => nft.metadata && nft.metadata.price && !nft.metadata.expired);
    }

    // Apply auctions-only filter
    if (showAuctionsOnly) {
      filtered = filtered.filter(nft => nft.metadata && (nft.metadata.marketType === 1 || nft.metadata.marketType === "AuctionMarket") && !nft.metadata.expired);
    }

    // Apply sorting
    if (sortOrder === 'low-high') {
      filtered.sort((a, b) => {
        const priceA = a.metadata?.price || Infinity;
        const priceB = b.metadata?.price || Infinity;
        return priceA - priceB;
      });
    } else if (sortOrder === 'high-low') {
      filtered.sort((a, b) => {
        const priceA = a.metadata?.price || -1;
        const priceB = b.metadata?.price || -1;
        return priceB - priceA;
      });
    }

    return filtered;
  }, [nfts, selectedTraits, showActiveOnly, showAuctionsOnly, sortOrder, searchQuery]);

  // Handlers
  const handlePrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const handleActivityPrevPage = () => setActivityPage(prev => Math.max(prev - 1, 1));
  const handleActivityNextPage = () => setActivityPage(prev => Math.min(prev + 1, totalActivityPages));
  const handleTransactionPrevPage = () => setTransactionPage(prev => Math.max(prev - 1, 1));
  const handleTransactionNextPage = () => setTransactionPage(prev => Math.min(prev + 1, totalTransactionPages));

  const handleTraitChange = (trait, value) => {
    setSelectedTraits(prev => ({ ...prev, [trait]: value }));
  };

  const handleSortOrderChange = (order) => {
    setSortOrder(order);
  };

  const handleActiveFilterChange = () => {
    setShowActiveOnly(!showActiveOnly);
  };

  const handleMarketplaceSelect = (id) => {
    setSelectedMarketplace(id);
    setMarketplaceDropdownVisible(false);
  };

  const clearFilters = () => {
    setSelectedTraits({});
    setSortOrder('default');
    setShowActiveOnly(false);
    setShowAuctionsOnly(false);
    setSearchQuery('');
  };

  const handleImageError = (event) => {
    const src = event.target.src;
    if (src.startsWith("https://klever-mint.mypinata.cloud/ipfs/")) {
      event.target.src = src.replace(
        "https://klever-mint.mypinata.cloud/ipfs/",
        "https://ipfs.io/ipfs/"
      );
    }
  };

  // Calculate listing percentage
  const listingPercentage = items > 0 ? ((listedCount / items) * 100).toFixed(1) : 0;

  return (
    <div className="collection-detail-page">
      {/* Hero Header */}
      <div 
        className="collection-header" 
        style={{ 
          backgroundImage: bannerUri ? `url(${bannerUri})` : gradientBackground 
        }}
      >
        <div className="header-overlay">
          <div className="header-content">
            <div className="collection-logo-wrapper">
              <img
                src={logoUrl}
                alt={assetName}
                className="collection-logo"
                onError={handleImageError}
              />
              <div className="logo-glow"></div>
            </div>
            
            <div className="collection-info">
              <h1 className="collection-title">{assetName || 'Loading...'}</h1>
              <p className="collection-id">Collection ID: {collectionId}</p>
              
              {/* Social Links */}
              {(websiteUri || telegramUri || discordUri) && (
                <div className="collection-social">
                  {websiteUri && (
                    <a href={websiteUri} target="_blank" rel="noopener noreferrer" className="social-link">
                      <img src={websiteLogo} alt="Website" />
                    </a>
                  )}
                  {telegramUri && (
                    <a href={telegramUri} target="_blank" rel="noopener noreferrer" className="social-link">
                      <img src={telegramLogo} alt="Telegram" />
                    </a>
                  )}
                  {discordUri && (
                    <a href={discordUri} target="_blank" rel="noopener noreferrer" className="social-link">
                      <img src={discordLogo} alt="Discord" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Stats Section */}
      <div className="collection-stats-container">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon volume-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Volume</div>
              <div className="stat-value">{(totalVolume / 1000000).toLocaleString()} KLV</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon floor-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Floor Price</div>
              <div className="stat-value">{floorPrice > 0 ? `${(floorPrice / 1000000).toFixed(2)} KLV` : 'N/A'}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon avg-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Avg Price</div>
              <div className="stat-value">{avgPrice > 0 ? `${(avgPrice / 1000000).toFixed(2)} KLV` : 'N/A'}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon items-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Items</div>
              <div className="stat-value">{items.toLocaleString()}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon listed-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Listed</div>
              <div className="stat-value">
                {listedCount} ({listingPercentage}%)
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon owners-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Owners</div>
              <div className="stat-value">{owners.toLocaleString()}</div>
            </div>
          </div>

          {royaltyInfo && (
            <div className="stat-card royalty-card">
              <div className="stat-icon royalty-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-label">Royalties</div>
                <div className="stat-value">{royaltyInfo.marketPercentage}%</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <nav className="tab-navigation">
        <div className="tab-container">
          <button
            className={`tab-item ${activeTab === 'items' ? 'active' : ''}`}
            onClick={() => setActiveTab('items')}
          >
            <svg className="tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
            </svg>
            Items
          </button>
          <button
            className={`tab-item ${activeTab === 'activity' ? 'active' : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            <svg className="tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            Activity
          </button>
          <button
            className={`tab-item ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <svg className="tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Analytics
          </button>
        </div>

        {activeTab === 'items' && (
          <div className="tab-extras">
            <button 
              className="icon-button"
              onClick={() => setFiltersVisible(!filtersVisible)}
              title="Toggle Filters"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
            </button>

            <div className="view-toggle">
              <button 
                className={`view-button ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid View"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                </svg>
              </button>
              <button 
                className={`view-button ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>

            <div className="marketplace-dropdown">
              <button
                className="dropdown-trigger"
                onClick={() => setMarketplaceDropdownVisible(!marketplaceDropdownVisible)}
              >
                <img src={marketplaceImages[selectedMarketplace]} alt="Marketplace" />
                {marketplaceNames[selectedMarketplace]}
                <svg className="dropdown-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {marketplaceDropdownVisible && (
                <div className="dropdown-content">
                  {Object.entries(marketplaceNames).map(([id, name]) => (
                    <div key={id} className="dropdown-item" onClick={() => handleMarketplaceSelect(id)}>
                      <img src={marketplaceImages[id]} alt={name} />
                      {name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="sub-tab-navigation">
            <button
              className={`sub-tab-item ${activeSubTab === 'sales' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('sales')}
            >
              Sales
            </button>
            <button
              className={`sub-tab-item ${activeSubTab === 'listings' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('listings')}
            >
              Listings
            </button>
          </div>
        )}
      </nav>

      {/* Main Content */}
      {activeTab === 'items' && (
        <section className="content-container">
          {/* Advanced Filters */}
          {filtersVisible && (
            <div className="filters-panel">
              <div className="filters-header">
                <h3>Filters</h3>
                <button className="clear-filters-btn" onClick={clearFilters}>
                  Clear All
                </button>
              </div>

              {/* Search Bar */}
              <div className="search-bar">
                <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by ID or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>

              <div className="filters-grid">
                {/* Trait Filters */}
                {Object.entries(traits).map(([trait, values]) => (
                  <div key={trait} className="filter-group">
                    <label className="filter-label">{trait}</label>
                    <select 
                      value={selectedTraits[trait] || ''} 
                      onChange={e => handleTraitChange(trait, e.target.value)}
                      className="filter-select"
                    >
                      <option value="">All {trait}</option>
                      {Array.from(values).map(value => (
                        <option key={value} value={value}>{value}</option>
                      ))}
                    </select>
                  </div>
                ))}

                {/* Sort Options */}
                <div className="filter-group">
                  <label className="filter-label">Sort By</label>
                  <select value={sortOrder} onChange={e => handleSortOrderChange(e.target.value)} className="filter-select">
                    <option value="default">Default</option>
                    <option value="low-high">Price: Low to High</option>
                    <option value="high-low">Price: High to Low</option>
                  </select>
                </div>

                {/* Active Only Checkbox */}
                <div className="filter-group checkbox-filter">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={showActiveOnly}
                      onChange={handleActiveFilterChange}
                      className="filter-checkbox"
                    />
                    <span className="checkbox-custom"></span>
                    Show Listed Only
                  </label>
                </div>

                <div className="filter-group checkbox-filter">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={showAuctionsOnly}
                      onChange={() => setShowAuctionsOnly(!showAuctionsOnly)}
                      className="filter-checkbox"
                    />
                    <span className="checkbox-custom"></span>
                    🔨 Show Auctions Only
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* NFT Grid/List */}
          <div className={`nft-display ${viewMode}`}>
            {loading ? (
              <div className="loading-container">
                <Spinner />
                <p className="loading-text">Loading collection...</p>
              </div>
            ) : filteredNfts.length ? (
              filteredNfts.map((nft, idx) => (
                <Link 
                  key={idx} 
                  to={`/collections/${collectionId}/${nft.nonce}`} 
                  className={`nft-card ${nft.metadata?.expired ? 'expired' : ''} ${viewMode}-view`}
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  <div className="nft-image-container">
                    {nft.loadingMetadata ? (
                      <div className="image-skeleton">
                        <Spinner />
                      </div>
                    ) : nft.metadata ? (
                      <>
                        <img 
                          src={nft.metadata.image} 
                          alt={nft.metadata.name || `#${nft.nonce}`} 
                          className="nft-image"
                          loading="lazy"
                          onError={handleImageError}
                        />
                        <div className="image-overlay">
                          <span className="view-details">View Details</span>
                        </div>
                      </>
                    ) : (
                      <div className="no-image">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    {nft.metadata?.expired && <div className="expired-badge">Expired</div>}
                    {(nft.metadata?.marketType === 1 || nft.metadata?.marketType === "AuctionMarket") && !nft.metadata?.expired && (
                      <div className="auction-badge">🔨 Auction</div>
                    )}
                  </div>
                  
                  <div className="nft-card-details">
                    <div className="nft-header">
                      <h3 className="collection-nft-name">
                        {nft.metadata?.name || `#${nft.nonce}`}
                      </h3>
                      <span className="nft-number">#{nft.nonce}</span>
                    </div>
                    
                    {nft.metadata && (
                      <div className="nft-footer">
                        <div className="price-section">
                          {nft.metadata.price ? (
                            <>
                              <span className="price-label">Price</span>
                              <span className="nft-price">{nft.metadata.price} KLV</span>
                            </>
                          ) : (
                            <span className="not-listed">Not Listed</span>
                          )}
                        </div>
                        {viewMode === 'list' && nft.metadata.attributes && (
                          <div className="nft-traits">
                            {nft.metadata.attributes.slice(0, 3).map((attr, i) => (
                              <span key={i} className="trait-badge">
                                {attr.trait_type}: {attr.value}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Link>
              ))
            ) : (
              <div className="no-items-container">
                <svg className="no-items-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p className="no-items-message">No items match your filters</p>
                <button className="reset-filters-btn" onClick={clearFilters}>
                  Reset Filters
                </button>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && filteredNfts.length > 0 && (
            <div className="pagination">
              <button 
                onClick={handlePrevPage} 
                disabled={currentPage <= 1}
                className="pagination-btn"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </button>
              <div className="pagination-info">
                <span className="current-page">{currentPage}</span>
                <span className="page-separator">/</span>
                <span className="total-pages">{totalPages}</span>
              </div>
              <button 
                onClick={handleNextPage} 
                disabled={currentPage >= totalPages}
                className="pagination-btn"
              >
                Next
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </section>
      )}

      {activeTab === 'activity' && (
        <section className="activity-section">
          {activeSubTab === 'sales' && (
            <>
              {activity.length === 0 ? (
                <div className="no-data-container">
                  <svg className="no-data-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p className="no-data-message">No sales yet</p>
                </div>
              ) : (
                <div className="activity-list">
                  {activity.map((item, idx) => {
                    const { contract, sender, marketplaceId, timestamp, assetId } = item;
                    const assetNumber = assetId ? assetId.split('/')[1] : 'Unknown';
                    const salePrice = contract.parameter.amount / 1000000;
                    const date = new Date(timestamp * 1000);
                    const marketplaceName = marketplaceNames[marketplaceId] || marketplaceId;
                    
                    return (
                      <Link 
                        key={idx} 
                        to={`/collections/${collectionId}/${assetNumber}`} 
                        className="activity-item"
                        style={{ animationDelay: `${idx * 0.05}s` }}
                      >
                        <div className="activity-image">
                          <img 
                            src={imageUrl && imageUrl.replace(/\/\d+\.png$/, `/${assetNumber}.png`)} 
                            alt={`Asset ${assetId}`}
                            onError={handleImageError}
                          />
                        </div>
                        <div className="activity-details">
                          <div className="activity-header">
                            <span className="activity-type sale">Sold</span>
                            <span className="activity-id">#{assetNumber}</span>
                          </div>
                          <div className="activity-info">
                            <span className="activity-price">{salePrice.toLocaleString('en-US')} KLV</span>
                            <span className="activity-marketplace">{marketplaceName}</span>
                          </div>
                          <div className="activity-meta">
                            <span className="activity-user">
                              Buyer: {sender === walletAddress ? 'You' : `${sender.slice(0, 8)}...${sender.slice(-4)}`}
                            </span>
                            <span className="activity-time">{date.toLocaleDateString()}</span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
              
              {activity.length > 0 && (
                <div className="pagination">
                  <button 
                    onClick={handleActivityPrevPage} 
                    disabled={activityPage <= 1}
                    className="pagination-btn"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Previous
                  </button>
                  <div className="pagination-info">
                    <span className="current-page">{activityPage}</span>
                    <span className="page-separator">/</span>
                    <span className="total-pages">{totalActivityPages}</span>
                  </div>
                  <button 
                    onClick={handleActivityNextPage} 
                    disabled={activityPage >= totalActivityPages}
                    className="pagination-btn"
                  >
                    Next
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
          
          {activeSubTab === 'listings' && (
            <div className="listing-section">
              {transactions.length === 0 ? (
                <div className="no-data-container">
                  <svg className="no-data-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <p className="no-data-message">No listings found</p>
                </div>
              ) : (
                <div className="activity-list">
                  {transactions.map((tx, idx) => {
                    const { contract, sender, marketplaceId, timestamp, isAuction, listingStatus } = tx;
                    const assetId = contract.parameter.assetId;
                    const assetNumber = assetId ? assetId.split('/')[1] : 'Unknown';
                    const listedPrice = contract.parameter.price / 1000000;
                    const reservePrice = contract.parameter.reservePrice ? contract.parameter.reservePrice / 1000000 : null;
                    const date = new Date(timestamp * 1000);
                    const marketplaceName = marketplaceNames[marketplaceId] || marketplaceId || 'Unknown';

                    // Determine status class and label
                    const statusClass = listingStatus === 'expired' ? 'expired' : 'listing';
                    const statusLabel = listingStatus === 'expired' ? 'Expired' : (isAuction ? 'Auction' : 'Active');

                    return (
                      <Link
                        key={idx}
                        to={`/collections/${collectionId}/${assetNumber}`}
                        className={`activity-item ${listingStatus === 'expired' ? 'expired-listing' : ''}`}
                        style={{ animationDelay: `${idx * 0.05}s` }}
                      >
                        <div className="activity-image">
                          <img
                            src={logoUrl}
                            alt={`Asset ${assetId}`}
                            onError={handleImageError}
                          />
                          {isAuction && listingStatus === 'active' && <div className="auction-badge">🔨</div>}
                          {listingStatus === 'expired' && <div className="expired-badge">⏱</div>}
                        </div>
                        <div className="activity-details">
                          <div className="activity-header">
                            <span className={`activity-type ${statusClass}`}>{statusLabel}</span>
                            <span className="activity-id">#{assetNumber}</span>
                          </div>
                          <div className="activity-info">
                            <span className="activity-price">
                              {isAuction && reservePrice ? `${reservePrice.toLocaleString('en-US')} KLV (Reserve)` :
                               `${listedPrice.toLocaleString('en-US')} KLV`}
                            </span>
                            <span className="activity-marketplace">{marketplaceName}</span>
                          </div>
                          <div className="activity-meta">
                            <span className="activity-user">
                              Seller: {sender === walletAddress ? 'You' : `${sender.slice(0, 8)}...${sender.slice(-4)}`}
                            </span>
                            <span className="activity-time">{date.toLocaleDateString()}</span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
              
              {transactions.length > 0 && (
                <div className="pagination">
                  <button 
                    onClick={handleTransactionPrevPage} 
                    disabled={transactionPage <= 1}
                    className="pagination-btn"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Previous
                  </button>
                  <div className="pagination-info">
                    <span className="current-page">{transactionPage}</span>
                    <span className="page-separator">/</span>
                    <span className="total-pages">{totalTransactionPages}</span>
                  </div>
                  <button 
                    onClick={handleTransactionNextPage} 
                    disabled={transactionPage >= totalTransactionPages}
                    className="pagination-btn"
                  >
                    Next
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <section className="analytics-section">
          <div className="analytics-grid">
            {/* Sales Volume Chart */}
            <div className="analytics-card chart-card">
              <h3 className="analytics-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="analytics-icon">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                Sales Volume (30 Days)
              </h3>
              <div className="chart-container">
                {salesHistory.length > 0 ? (
                  <Line
                    data={{
                      labels: salesHistory.map(d => {
                        const date = new Date(d.date);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }),
                      datasets: [
                        {
                          label: 'Volume (KLV)',
                          data: salesHistory.map(d => d.volume),
                          borderColor: '#6366f1',
                          backgroundColor: 'rgba(99, 102, 241, 0.1)',
                          fill: true,
                          tension: 0.4,
                          pointRadius: 3,
                          pointBackgroundColor: '#6366f1',
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          padding: 12,
                          titleFont: { size: 13 },
                          bodyFont: { size: 12 },
                          callbacks: {
                            label: (context) => `${context.parsed.y.toLocaleString()} KLV`
                          }
                        }
                      },
                      scales: {
                        x: {
                          grid: { color: 'rgba(255, 255, 255, 0.05)' },
                          ticks: { color: '#9ca3af', font: { size: 10 } }
                        },
                        y: {
                          grid: { color: 'rgba(255, 255, 255, 0.05)' },
                          ticks: {
                            color: '#9ca3af',
                            font: { size: 10 },
                            callback: (value) => value >= 1000 ? `${(value/1000).toFixed(0)}K` : value
                          }
                        }
                      }
                    }}
                  />
                ) : (
                  <div className="no-chart-data">
                    <p>No sales data available for this period</p>
                  </div>
                )}
              </div>
            </div>

            {/* Floor Price Chart */}
            <div className="analytics-card chart-card">
              <h3 className="analytics-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="analytics-icon">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                Floor Price History (30 Days)
              </h3>
              <div className="chart-container">
                {salesHistory.length > 0 ? (
                  <Line
                    data={{
                      labels: salesHistory.map(d => {
                        const date = new Date(d.date);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }),
                      datasets: [
                        {
                          label: 'Floor Price (KLV)',
                          data: salesHistory.map(d => d.floor),
                          borderColor: '#10b981',
                          backgroundColor: 'rgba(16, 185, 129, 0.1)',
                          fill: true,
                          tension: 0.4,
                          pointRadius: 3,
                          pointBackgroundColor: '#10b981',
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          padding: 12,
                          callbacks: {
                            label: (context) => `${context.parsed.y.toLocaleString()} KLV`
                          }
                        }
                      },
                      scales: {
                        x: {
                          grid: { color: 'rgba(255, 255, 255, 0.05)' },
                          ticks: { color: '#9ca3af', font: { size: 10 } }
                        },
                        y: {
                          grid: { color: 'rgba(255, 255, 255, 0.05)' },
                          ticks: { color: '#9ca3af', font: { size: 10 } }
                        }
                      }
                    }}
                  />
                ) : (
                  <div className="no-chart-data">
                    <p>No floor price data available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Top Holders */}
            <div className="analytics-card holders-card">
              <h3 className="analytics-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="analytics-icon">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Top Collectors
              </h3>
              <div className="holders-list">
                {topHolders.length > 0 ? (
                  topHolders.map((holder, idx) => (
                    <div key={holder.address} className="holder-item">
                      <span className="holder-rank">#{idx + 1}</span>
                      <span className="holder-address">
                        {holder.address.slice(0, 10)}...{holder.address.slice(-6)}
                      </span>
                      <span className="holder-count">{holder.count} NFTs</span>
                    </div>
                  ))
                ) : (
                  <div className="no-holders-data">
                    <p>No holder data available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Sales Stats */}
            <div className="analytics-card stats-summary-card">
              <h3 className="analytics-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="analytics-icon">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                30-Day Summary
              </h3>
              <div className="stats-summary-grid">
                <div className="summary-stat">
                  <span className="summary-label">Total Sales</span>
                  <span className="summary-value">
                    {salesHistory.reduce((acc, d) => acc + d.sales, 0)}
                  </span>
                </div>
                <div className="summary-stat">
                  <span className="summary-label">Total Volume</span>
                  <span className="summary-value">
                    {salesHistory.reduce((acc, d) => acc + d.volume, 0).toLocaleString()} KLV
                  </span>
                </div>
                <div className="summary-stat">
                  <span className="summary-label">Avg Sale Price</span>
                  <span className="summary-value">
                    {salesHistory.length > 0
                      ? (salesHistory.reduce((acc, d) => acc + d.avgPrice, 0) / salesHistory.length).toFixed(0)
                      : 0} KLV
                  </span>
                </div>
                <div className="summary-stat">
                  <span className="summary-label">Active Days</span>
                  <span className="summary-value">{salesHistory.length}</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

const getMarketplaceClassName = (marketplaceId) => {
  const classMap = {
    '7c353c02770da029': 'marketplace-sky-gallery',
    'd2a04fe890a6edda': 'marketplace-nft-art-gallery',
    'd4f2bab340c55fde': 'marketplace-klevernft',
    '2936933ee43db509': 'marketplace-minthree',
    '417b70c0eb7a33cb': 'marketplace-rare-canvas',
    'aa68ad853a09988c': 'marketplace-xport',
    '116056b257d9f6d5': 'marketplace-kDEX',
    '81376f526cf47730': 'marketplace-world',
  };
  return classMap[marketplaceId] || '';
};

export default CollectionDetailPage;